import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import styles from './styles.module.css';
import { Settings, Info as InfoIcon } from 'lucide-react';
import integrationData from './integrations';
import * as translations from './strings';
import { getLangKey } from '../../../models/Language';
import { Button, Tooltip } from 'antd';
import _debounce from 'lodash/debounce';
import { post } from '../../../utils/fetch';
import cx from 'classnames';
import LinkTask from './LinkTask';
import Connected from './Connected';
import Error from './Error';
import { Integration } from '../types';
import HarvestIframe from './HarvestIframe';
import { onArticleClick } from '../../../utils/HelpArticleButton';
import { IntegrationMessage } from '../integrationMessages';

type Props = {
  name: string;
  integration?: Integration;
  projectName?: string;
  link?: string;
  container: HTMLDivElement;
  projectId?: number;
  isAdminView?: boolean;
  bugherdUrl: string;
  apiDomain?: string;
  taskId: number;
  taskLocalId: number;
  error?: {
    code: number;
    message: string;
    created_at: string;
  };
  isSmallView?: boolean;
  integrationType?: string;
  hasQueryWorkflow?: boolean;
  loading?: boolean;
  showHelp?: boolean;
  harvestEmbedUrl?: string;
  projectIntegrationId?: number;
  integrationId?: number;
  organizationId: number;
  settings?: {
    organizationId?: string;
    organization_id?: string;
    project?: { label?: string; value?: string };
    site_name?: string;
    site_url?: string;
    siteId?: { label: string };
  };
  remoteOrganizationId?: string;
  taskConnectionId?: number;
  remoteTaskId?: string;
  remoteTaskMeta?: Record<string, any>;
  integrationMessageError?: IntegrationMessage;
  refreshIntegrationMessages?: () => void;
};

const strings = translations[getLangKey()];
const PRODUCTIVE_BASE_URL = 'https://app.productive.io';

export type Parent = { id: string; name: string };

const Card: FC<Props> = ({
  name,
  integration,
  projectName,
  container,
  projectId,
  isAdminView,
  bugherdUrl,
  apiDomain,
  taskId,
  taskLocalId,
  error,
  isSmallView,
  integrationType,
  hasQueryWorkflow,
  loading,
  showHelp,
  harvestEmbedUrl,
  projectIntegrationId,
  integrationId,
  organizationId,
  settings = {},
  remoteOrganizationId,
  taskConnectionId,
  remoteTaskId,
  remoteTaskMeta,
  integrationMessageError,
  refreshIntegrationMessages
}) => {
  const nativeTaskBasedIntegrations = [
    'asana',
    'jira',
    'linear',
    'trello',
    'clickup',
    'monday',
    'productive'
  ];
  const taskBasedNativeIntegration: boolean =
    integrationType === 'native' && nativeTaskBasedIntegrations.includes(name);
  const {
    label,
    connectedText,
    viewIn,
    linkText,
    createText,
    link,
    helpLink,
    helpId
  } = integrationData[name] || integrationData['generic'];
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [taskToLink, setTaskToLink] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const [connecting, setConnecting] = useState<boolean>(false);
  const [parentTasks, setParentTasks] = useState<Parent[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [queryError, setQueryError] = useState<string>('');
  const [createError, setCreateError] = useState<string>('');
  const [connectionRemoved, setConnectionRemoved] = useState<boolean>(false);
  const [retrying, setRetrying] = useState<boolean>(false);
  const retryErrorId = useRef<number | undefined>(undefined);
  const githubRepository =
    name === 'github'
      ? settings.project?.label || settings.project?.value || projectName
      : undefined;
  const connectedProjectName = githubRepository || projectName;
  const connected: boolean =
    !connectionRemoved &&
    (taskBasedNativeIntegration
      ? !!remoteTaskId
      : !!(integration?.id || connectedProjectName || remoteTaskId));

  useEffect(() => {
    if (integration) {
      setConnecting(false);
    }
  }, [integration]);

  useEffect(() => {
    setConnectionRemoved(false);
    if (remoteTaskId) {
      setRetrying(false);
      setConnecting(false);
    }
  }, [integration?.id, projectName, remoteTaskId]);

  useEffect(() => {
    if (retrying && integrationMessageError?.id !== retryErrorId.current) {
      setRetrying(false);
      setConnecting(false);
    }
  }, [integrationMessageError?.id, retrying]);

  useEffect(() => {
    if (!retrying || !refreshIntegrationMessages) return;

    refreshIntegrationMessages();
    const refreshInterval = window.setInterval(
      refreshIntegrationMessages,
      2000
    );
    const retryTimeout = window.setTimeout(() => {
      setRetrying(false);
      setConnecting(false);
    }, 30000);

    return () => {
      window.clearInterval(refreshInterval);
      window.clearTimeout(retryTimeout);
    };
  }, [refreshIntegrationMessages, retrying]);

  type QueryResponse = {
    statusCode: number;
    results: Parent[];
  };

  const queryTasks = (search: string) => {
    if (search && (!taskToLink || taskToLink !== search)) {
      setSearching(true);
      setQueryError('');

      const nativeBody: {
        search: string;
        integration: string;
        is_key?: boolean;
        task_id: number;
        project_integration_id: number | undefined;
      } = {
        search,
        integration: name,
        task_id: taskId,
        project_integration_id: projectIntegrationId
      };

      const filterTasks = (previousTasks, results) => {
        return results.filter(
          result => !previousTasks.find(task => task.id === result.id)
        );
      };

      if (integrationType === 'native' && name === 'jira') {
        post(
          `${apiDomain ||
            bugherdUrl}/organizations/${organizationId}/integrations/${integrationId}/project_integrations/search_for_task`,
          nativeBody
        )
          .then((res: QueryResponse) => {
            setParentTasks(previousTasks =>
              previousTasks.concat(filterTasks(previousTasks, res))
            );
            setSearching(false);
          })
          .catch(err => {
            setQueryError(err.message || err);
            console.error(err);
          });
      }
    }
  };

  const debounceFn = useCallback(_debounce(queryTasks, 1500), []);

  const handleCreateIntegrationTask = () => {
    setSending(true);
    setCreateError('');
    if (
      name === 'teamwork' ||
      (integrationType === 'native' &&
        nativeTaskBasedIntegrations.includes(name))
    ) {
      post(
        `${apiDomain ||
          bugherdUrl}/organizations/${organizationId}/integrations/${integrationId}/project_integrations/manually_create_task`,
        {
          task_id: taskId,
          project_integration_id: projectIntegrationId,
          parent_id: taskToLink || undefined
        }
      )
        .then(() => {
          setSending(false);
          setConnecting(true);
          setTaskToLink('');
        })
        .catch(err => {
          setSending(false);
          setConnecting(false);
          setRetrying(false);
          setCreateError(err.message || err);
          console.error(err);
        });
    }
    if (showSearch) setShowSearch(false);
  };

  const handleRetry = () => {
    retryErrorId.current = integrationMessageError?.id;
    setRetrying(true);
    handleCreateIntegrationTask();
  };

  const handleCancel = () => {
    setSearch('');
    setShowSearch(false);
    setTaskToLink('');
    setShowDropdown(false);
    setSearching(false);
  };

  const handleShowSettings = () => {
    if (isAdminView) {
      // eslint-disable-next-line no-unused-expressions
      showHelp
        ? undefined
        : // @ts-ignore
          window?.integration_settings_props?.setShow(true);
    } else if (showHelp) {
      // eslint-disable-next-line no-unused-expressions
      helpLink && window.open(helpLink, '_blank');
    } else {
      window.open(
        `${bugherdUrl}/projects/${projectId}/kanban?open=integration_settings`,
        '_blank'
      );
    }
  };

  const showErrorState = !sending && (!!error?.message || !!createError);

  const teamworkBaseUrl = () => {
    const siteUrl = settings.siteUrl || settings.siteName || '';
    const trimmedUrl = siteUrl.trim().replace(/\/+$/, '');

    if (!trimmedUrl) return '';
    if (/^https?:\/\//i.test(trimmedUrl)) return trimmedUrl;

    return `https://${
      trimmedUrl.includes('.') ? trimmedUrl : `${trimmedUrl}.teamwork.com`
    }`;
  };

  const teamworkTaskLink = () => {
    return `${teamworkBaseUrl()}/app/tasks/${remoteTaskId}`;
  };

  const jiraTaskLink = () => {
    if (!settings.siteId?.label || !remoteTaskMeta?.key) return '';

    return `https://${settings.siteId.label}.atlassian.net/browse/${remoteTaskMeta.key}`;
  };

  const clickupTaskLink = () => {
    return `https://app.clickup.com/t/${remoteTaskId}`;
  };

  const asanaTaskLink = () => {
    return `https://app.asana.com/0/0/${remoteTaskId}`;
  };

  const productiveTaskLink = () => {
    const organizationId =
      settings?.organizationId ||
      settings?.organization_id ||
      remoteOrganizationId;
    const productiveProjectId = settings?.project?.value;

    if (!organizationId || !productiveProjectId || !remoteTaskId) return '';

    return `${PRODUCTIVE_BASE_URL}/${organizationId}/projects/${productiveProjectId}/tasks/task/${remoteTaskId}`;
  };

  const connectedLink: string =
    integration?.url ||
    remoteTaskMeta?.url ||
    (connectedProjectName && link ? link(connectedProjectName) : '') ||
    (name === 'teamwork' && remoteTaskId ? teamworkTaskLink() : '') ||
    (name === 'asana' && remoteTaskId ? asanaTaskLink() : '') ||
    (name === 'clickup' && remoteTaskId ? clickupTaskLink() : '') ||
    (name === 'productive' && remoteTaskId ? productiveTaskLink() : '') ||
    (name === 'jira' && remoteTaskId ? jiraTaskLink() : '');

  const storedIntegrationError = integrationMessageError && {
    message: integrationMessageError.message || integrationMessageError.subject,
    created_at: integrationMessageError.created_at
  };

  const connectedContent = (
    <Connected
      taskId={taskId}
      taskLocalId={taskLocalId}
      isSmallView={!!isSmallView}
      connectedText={connectedText}
      buttonText={viewIn || integration?.id || connectedProjectName}
      href={connectedLink}
      projectId={projectId}
      taskConnectionId={taskConnectionId}
      error={storedIntegrationError || undefined}
      onRemoved={() => setConnectionRemoved(true)}
    />
  );

  const getBottomContent = () => {
    if (loading) return strings.loadingIntegration;

    if (name === 'harvest' && harvestEmbedUrl) {
      return <HarvestIframe url={harvestEmbedUrl} />;
    }
    if (connected) {
      return connectedContent;
    }
    if (storedIntegrationError || showErrorState) {
      return (
        <Error
          error={createError ? undefined : storedIntegrationError || error}
          createError={createError}
          container={container}
          onRetry={handleRetry}
          retrying={retrying}
        />
      );
    }

    return (
      <LinkTask
        container={container}
        searching={searching}
        search={search}
        handleOnChange={(searchValue: string) => {
          setSearch(searchValue);
          debounceFn(searchValue);
        }}
        handleOnSelect={(parentName: string) => {
          setSearch(parentName);

          const taskId = parentTasks.find(task => task.name === parentName)?.id;
          if (taskId) setTaskToLink(taskId);
        }}
        parentTasks={parentTasks.filter(
          ({ id, name }) =>
            id.toLowerCase().includes(search.toLowerCase()) ||
            name.toLowerCase().includes(search.toLowerCase())
        )}
        showDropdown={showDropdown}
        setShowDropdown={setShowDropdown}
        handleCreateIntegrationTask={handleCreateIntegrationTask}
        linkingDisabled={!taskToLink}
        handleCancel={handleCancel}
        linkText={linkText}
        isSmallView={!!isSmallView}
        createText={createText}
        showSearch={showSearch}
        hasQueryWorkflow={!!hasQueryWorkflow}
        setShowSearch={setShowSearch}
        sending={sending}
        queryError={queryError}
        connecting={connecting}
      />
    );
  };

  const configButton = (
    <Tooltip
      getPopupContainer={() => container}
      overlayClassName={styles.tooltip}
      title={showHelp ? strings.info : strings.setupHere}
      trigger={['hover']}
    >
      <Button
        className={styles.settingsButton}
        type="text"
        icon={
          showHelp ? <InfoIcon /> : <Settings className={styles.settingsIcon} />
        }
        onClick={() => {
          if (isAdminView && showHelp && helpId) {
            onArticleClick({ articleId: helpId });
          }
          handleShowSettings();
        }}
      />
    </Tooltip>
  );

  return (
    <div className={styles.cardOuter}>
      <div className={styles.cardTop}>
        <div className={styles.integration}>
          <span
            className={cx(
              styles.integrationLogo,
              styles[`${name.replace(/\./g, '')}Logo`]
            )}
          />
          <span className={styles.integrationLabel}>{label}</span>
        </div>
        {configButton}
      </div>
      <div
        className={cx(styles.cardBottom, {
          [styles.noPadding]: name === 'harvest'
        })}
      >
        {getBottomContent()}
      </div>
    </div>
  );
};

export default Card;
